import { useCallback } from "react";

import { useStorePickerContext } from "@/components/Store/StorePicker/storePicker.context";
import { StorePickerType } from "@/components/Store/StorePicker/types/storePickerType.enum";
import { getStoreInfo } from "@/components/Store/utils/storeInfo";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";
import { shouldDisableAddToCart } from "@/utils/disableAddToCart";

import { StoreCardComponent } from "./storeCard.component";

import "./storeCard.scss";
import { CloudStockLevel } from "@omni/types/api/product/cloud/shared/enums/stockLevel.enum";
import { CloudStore } from "@omni/types/api/store/cloud/store.model";

type StoreCardContainerProps = {
    store: CloudStore;
};

const StoreCardContainer = ({ store }: StoreCardContainerProps) => {
    const { t } = useGranite();
    const { selectedStore, setSelectedStore, config, submitWithStore } =
        useStorePickerContext();

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    const isDisabled = useCallback(() => {
        if (
            [StorePickerType.SELECT, StorePickerType.SELECT_DRIVE_IN].includes(
                config.type
            )
        ) {
            return false;
        }

        if (!config.displayStockLevel) return false;

        if (shouldDisableAddToCart(store?.productAvailability)) {
            return true;
        }

        const stockLevel = store.productAvailability?.stockLevel;

        return (
            stockLevel === CloudStockLevel.NoStock ||
            stockLevel === CloudStockLevel.UnknownStock
        );
    }, [selectedStore, config]);

    const address = `${store.address.streetName} ${store.address.streetNumber}, ${store.address.postalCode} ${store.address.town}`;

    const selected = store.code === selectedStore?.code;

    const handleOnStoreClick = () => {
        if (isDisabled()) {
            return;
        }
        if (
            config.type === StorePickerType.SELECT ||
            config.type === StorePickerType.SELECT_DRIVE_IN
        ) {
            handleSubmit();
        }
        if (config.type !== StorePickerType.INFO) {
            setSelectedStore(store);
        }
    };

    const handleSubmit = () => {
        submitWithStore(store);
    };

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    const getOpenInfo = useCallback(
        (storeDetails: CloudStore) => getStoreInfo(storeDetails, t),
        [store]
    );

    const storeStatus = getOpenInfo(store);

    const statusText = t(
        `form.searchstore.storeCard.status.${storeStatus.isOpen ? "open" : "closed"}`
    );

    const isDisabledStore = isDisabled();

    return (
        <StoreCardComponent
            storeName={store.shopName}
            isDisabled={isDisabledStore}
            isSelected={selected}
            handleOnStoreClick={handleOnStoreClick}
            handleSubmit={handleSubmit}
            storeStatus={storeStatus}
            statusText={statusText}
            address={address}
            productAvailability={store.productAvailability}
            config={config}
        />
    );
};

export { StoreCardContainer };
